import React, { useState } from 'react';
import { Badge, Button } from '@fluentui/react-components';
import {
  ExceptionCase,
  TimelineEvent,
  EntityNode,
  EntityEdge,
  CaseStatus,
  Severity,
} from '../../data/mockExceptionData';
import styles from './ExceptionWorkspace.module.css';

// ─── Sub-panel: Related Entities ─────────────────────────────────────────────

const RelatedEntitiesPanel: React.FC<{ nodes: EntityNode[]; edges: EntityEdge[] }> = ({ nodes, edges }) => (
  <div className={styles.section}>
    <div className={styles.sectionTitle}>🔗 Related Entities</div>
    <div className={styles.entityGrid}>
      {nodes.map(n => (
        <div key={n.entity_id} className={styles.entityCard}>
          <div className={styles.entityType}>{n.entity_type}</div>
          <div className={styles.entityName}>{n.display_name}</div>
        </div>
      ))}
    </div>
    {edges.length > 0 && (
      <div className={styles.edgeList}>
        {edges.map((e, i) => (
          <div key={i} className={styles.edgeItem}>
            {e.source} → <em>{e.relationship.replace(/_/g, ' ')}</em> → {e.target}
          </div>
        ))}
      </div>
    )}
  </div>
);

// ─── Sub-panel: Agent Trace Timeline ─────────────────────────────────────────

const AgentTraceTimeline: React.FC<{ events: TimelineEvent[] }> = ({ events }) => {
  const traceEvents = events.filter(e => e.type === 'agent_trace');
  if (traceEvents.length === 0) return <div className={styles.section}><div className={styles.sectionTitle}>🤖 Agent Traces</div><span style={{ fontSize: 13, color: '#8a8886' }}>No traces yet</span></div>;
  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>🤖 Agent Traces ({traceEvents.length})</div>
      <div className={styles.timeline}>
        {traceEvents.map((ev, i) => (
          <div key={i} className={styles.timelineItem}>
            <span className={styles.timelineType}>{ev.data.agent_name}</span>
            <span className={styles.timelineTime}>{ev.data.operation}</span>
            <div className={styles.timelineContent}>{ev.data.output_summary}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Sub-panel: Evidence ─────────────────────────────────────────────────────

const EvidencePanel: React.FC<{ events: TimelineEvent[] }> = ({ events }) => {
  const evidenceEvents = events.filter(e => e.type === 'evidence_collected');
  if (evidenceEvents.length === 0) return null;
  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>📄 Evidence ({evidenceEvents.length})</div>
      {evidenceEvents.map((ev, i) => (
        <div key={i} className={styles.actionCard}>
          <div className={styles.actionTitle}>{ev.data.title}</div>
          <div className={styles.actionStatus}>{ev.data.evidence_type} — {ev.data.content}</div>
        </div>
      ))}
    </div>
  );
};

// ─── Sub-panel: Recommended Actions ──────────────────────────────────────────

const RecommendedActionsPanel: React.FC<{ events: TimelineEvent[]; onStartWorkflow: () => void }> = ({ events, onStartWorkflow }) => {
  const actionEvents = events.filter(e => e.type === 'action');
  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>⚡ Recommended Actions</div>
      {actionEvents.length === 0 && (
        <Button appearance="primary" size="small" onClick={onStartWorkflow}>Start Investigation Workflow</Button>
      )}
      {actionEvents.map((ev, i) => (
        <div key={i} className={styles.actionCard}>
          <div className={styles.actionTitle}>{ev.data.action_type?.replace(/_/g, ' ') || ev.data.description}</div>
          <div className={styles.actionStatus}>Status: {ev.data.status}</div>
          {ev.data.description && <div className={styles.actionStatus}>{ev.data.description}</div>}
        </div>
      ))}
    </div>
  );
};

// ─── Sub-panel: HITL Approval Status ─────────────────────────────────────────

const HITLApprovalStatus: React.FC<{ events: TimelineEvent[] }> = ({ events }) => {
  const hitlEvents = events.filter(e => e.type === 'hitl_request');
  if (hitlEvents.length === 0) return null;
  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>👤 HITL Approval</div>
      {hitlEvents.map((ev, i) => (
        <div key={i} className={`${styles.hitlCard} ${ev.data.status === 'pending' ? styles.hitlPending : ''}`}>
          <div className={styles.actionTitle}>{ev.data.title}</div>
          <div className={styles.actionStatus}>
            Status: <Badge size="small" appearance="filled" color={ev.data.status === 'pending' ? 'warning' : 'success'}>{ev.data.status}</Badge>
          </div>
          {ev.data.options && (
            <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
              {ev.data.options.map((opt: string) => (
                <Button key={opt} size="small" appearance={opt === 'approve' ? 'primary' : 'outline'}>{opt}</Button>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ─── Sub-panel: Decision Trace ───────────────────────────────────────────────

const DecisionTracePanel: React.FC<{ events: TimelineEvent[] }> = ({ events }) => {
  const decisionEvents = events.filter(e => e.type === 'decision');
  if (decisionEvents.length === 0) return null;
  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>⚖️ Decision Trace ({decisionEvents.length})</div>
      {decisionEvents.map((ev, i) => (
        <div key={i} className={styles.decisionCard}>
          <div className={styles.decisionAction}>
            {ev.data.action} <Badge size="small" appearance="outline" color="informative">{ev.data.decision_source}</Badge>
          </div>
          <div className={styles.decisionReasoning}>{ev.data.reasoning}</div>
          {ev.data.owner && <div className={styles.decisionReasoning}>By: {ev.data.owner}</div>}
        </div>
      ))}
    </div>
  );
};

// ─── Sub-panel: Outcome Memory ───────────────────────────────────────────────

const OutcomeMemoryPanel: React.FC<{ events: TimelineEvent[] }> = ({ events }) => {
  const outcomeEvents = events.filter(e => e.type === 'outcome');
  if (outcomeEvents.length === 0) return null;
  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>✅ Outcome</div>
      {outcomeEvents.map((ev, i) => (
        <div key={i} className={styles.outcomeCard}>
          <div className={styles.outcomeSummary}>{ev.data.resolution_summary}</div>
          <div className={styles.outcomeStats}>
            {ev.data.total_duration_hours && <span>Duration: {ev.data.total_duration_hours}h</span>}
            {ev.data.human_touches !== undefined && <span>Human touches: {ev.data.human_touches}</span>}
            {ev.data.final_status && <Badge size="small" appearance="filled" color="success">{ev.data.final_status}</Badge>}
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Main Detail Component ───────────────────────────────────────────────────

type Tab = 'timeline' | 'entities' | 'actions';

const statusColor: Record<CaseStatus, 'informative' | 'warning' | 'success' | 'danger' | 'important' | 'subtle' | 'brand'> = {
  detected: 'informative',
  investigating: 'brand',
  waiting_on_owner: 'warning',
  approval_needed: 'important',
  action_taken: 'brand',
  resolved: 'success',
  escalated: 'danger',
  closed: 'subtle',
  written_off: 'subtle',
};

const severityColor: Record<Severity, 'danger' | 'warning' | 'important' | 'subtle'> = {
  critical: 'danger',
  high: 'important',
  medium: 'warning',
  low: 'subtle',
};

interface Props {
  caseData: ExceptionCase;
  timeline: TimelineEvent[];
  entities: { nodes: EntityNode[]; edges: EntityEdge[] };
  onStartWorkflow: () => void;
}

const ExceptionCaseDetail: React.FC<Props> = ({ caseData, timeline, entities, onStartWorkflow }) => {
  const [activeTab, setActiveTab] = useState<Tab>('timeline');

  return (
    <div>
      {/* Header */}
      <div className={styles.detailHeader}>
        <div className={styles.detailTitle}>{caseData.title}</div>
        <div className={styles.detailDescription}>{caseData.description}</div>
        <div className={styles.detailBadges}>
          <Badge appearance="filled" color={statusColor[caseData.status]}>{caseData.status.replace(/_/g, ' ')}</Badge>
          <Badge appearance="outline" color={severityColor[caseData.severity]}>{caseData.severity}</Badge>
          <Badge appearance="outline" color="informative">{caseData.workflow_type.replace(/_/g, ' ')}</Badge>
          <Badge appearance="outline" color="subtle">{caseData.entity_type}: {caseData.entity_id}</Badge>
        </div>
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        <div className={`${styles.tab} ${activeTab === 'timeline' ? styles.tabActive : ''}`} onClick={() => setActiveTab('timeline')}>Timeline & Traces</div>
        <div className={`${styles.tab} ${activeTab === 'entities' ? styles.tabActive : ''}`} onClick={() => setActiveTab('entities')}>Entities & Context</div>
        <div className={`${styles.tab} ${activeTab === 'actions' ? styles.tabActive : ''}`} onClick={() => setActiveTab('actions')}>Actions & Decisions</div>
      </div>

      {/* Tab content */}
      {activeTab === 'timeline' && (
        <>
          <AgentTraceTimeline events={timeline} />
          <EvidencePanel events={timeline} />
        </>
      )}
      {activeTab === 'entities' && (
        <RelatedEntitiesPanel nodes={entities.nodes} edges={entities.edges} />
      )}
      {activeTab === 'actions' && (
        <>
          <RecommendedActionsPanel events={timeline} onStartWorkflow={onStartWorkflow} />
          <HITLApprovalStatus events={timeline} />
          <DecisionTracePanel events={timeline} />
          <OutcomeMemoryPanel events={timeline} />
        </>
      )}
    </div>
  );
};

export default ExceptionCaseDetail;
